import {
  useState,
  useContext,
  useEffect,
  useMemo,
  useRef,
  KeyboardEvent,
  useCallback
} from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { sortBy } from 'lodash';
import Masonry from 'react-masonry-css';
import {
  HeaderFixed,
  FlyerCard,
  EmptyState,
  BottomViewFlyers,
  LoadingSpinner,
  ViewMoreCard,
  Divider,
  NoSearchResultsFoundEmptyState,
  NoActiveFlyersFound,
} from '@/components';
import {
  APP_ROUTES,
  FlyerType,
  MasonryBreakpointCols,
  PAGE_SIZE,
} from '@/config/constants';
import FlyersContext from '@/context/FlyersContext';
import {
  AllFlyerFilters,
  FlyerCardThumbnail,
  GetAllFlyersResponse,
  OwningTypeEnum,
} from '@/graphql/types';
import turtleIllustration from '@/assets/turtle.svg';
import { useAnalytics } from '@/hooks/useAnalytics';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import {
  NavbarSwitcher,
  FlyerboardHeader,
  BodyLargeEmphasized,
} from 'design-system';
import styles from './ExploreResults.styles';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { subscriptionsVar, filterFacetsVar, flyerboardActiveFiltersVar, flyerboardAllFiltersVar, flyerboardFlyersAmountVar } from '@/graphql/state';
import { GET_ALL_FLYERS } from '@/graphql/queries';

import {
  districtVar,
} from '@/graphql/state';
import { useIsVisible, useMetrics } from '@/hooks';
import colors from '@/theme/colors';
import { FlyerStatus } from '@/types/flyer';
import UserPreferencesContext from '@/context/UserPreferencesContext';


export type FilterType = {
  [prop: string]: Array<any>;
};

export type FilterResults = {
  [prop: string]: Array<any> | null;
};

export const ExploreResults: React.FC = () => {
  const { search } = useParams();
  const {
    activeTab,
    onChangeFlyerType,
  } = useContext(FlyersContext);
  const ref = useRef<HTMLDivElement | null>(null);
  const isPageVisible = useIsVisible(ref);
  const { track } = useAnalytics();
  const { trackImpressions } = useMetrics();
  const navigate = useNavigate();
  const { search: locationSearch } = useLocation();
  const audienceId =
    new URLSearchParams(locationSearch).get('audienceId') || 'all';

  const { viewWholeCommunityFlyers } = useContext(UserPreferencesContext);
  const [getAllFlyers] = useLazyQuery<GetAllFlyersResponse>(GET_ALL_FLYERS)
  const [loading, setLoading] = useState<boolean>(false);
  const [flyers, setFlyers] = useState<FlyerCardThumbnail[]>([]);
  const [expiredFlyers, setExpiredFlyers] = useState<FlyerCardThumbnail[]>([]);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [expiredFlyersTotal, setExpiredFlyersTotal] = useState<number>(0);
  const [triggerFetch, setTriggerFetch] = useState(false);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [textSearch, setTextSearch] = useState(search || '');
  const [searchTerm, setSearchTerm] = useState(search || '');
  const [showExpiredFlyers, setShowExpiredFlyers] = useState<boolean>(false);
  const [shouldRequestExpiredFlyers, setShouldRequestExpiredFlyers] = useState<boolean>(false);
  const [dataNotFound, setDataNotFound] = useState<boolean>(false);
  const pages = useMemo(() =>
    Math.ceil(isExpired ? expiredFlyersTotal : total / PAGE_SIZE),
    [total, isExpired, expiredFlyersTotal]
  );
  const district = useReactiveVar(districtVar);
  const subscriptions = useReactiveVar(subscriptionsVar);
  const flyerboardActiveFilters = useReactiveVar(flyerboardActiveFiltersVar);

  const trackId = 'explore_results_page';

  const isFiltering = useMemo(() =>
    flyerboardActiveFilters.grades_age.length > 0 || flyerboardActiveFilters.types.length > 0,
  [flyerboardActiveFilters]);

  const textSearchNotFound = flyers.length === 0 && searchTerm.length > 0;

  const clearActiveFilters = () => {
    flyerboardActiveFiltersVar({
      grades_age: [],
      types: [],
    })
  };

  useEffect(() => {
    // reset the filters on mount
    if(isPageVisible){
      filterFacetsVar({
        categories: [],
        gradesOrAges: [],
      });
     setTriggerFetch(prev => !prev);
    }
  }, [isPageVisible]);

  const resetPaginationStates = () => {
    setIsExpired(false);
    setShouldRequestExpiredFlyers(false);
    setTotal(0);
    setExpiredFlyersTotal(0);
    setFlyers([]);
    setPage(1);
    setExpiredFlyers([]);
    setDataNotFound(false);
  }


  useEffect(() => {
    if(!isPageVisible) return;
    //reset pagination and filters
    resetPaginationStates();
    setTriggerFetch(prev => !prev);
  }, [activeTab, flyerboardActiveFilters, isPageVisible]);

  useEffect(() => {
    if (!district || !isPageVisible) return;
    // check if should show the expired saved flyers
      fetchData();
  }, [page, district, triggerFetch, isPageVisible]);

  useEffect(() => {
    setPage(1)
  }, [isExpired])

  const onKeyDown = (
    e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.key === 'Enter' && textSearch.length > 0) {
      setSearchTerm(textSearch);
      resetPaginationStates();
      clearActiveFilters();
      const encodedSearch = encodeURIComponent(textSearch);
      navigate(`/explore/${encodedSearch}/results`, { replace: true });
    }
  };

  const fetchData =  async() => {
    if((pages > 0 && page > pages) || !district) {
      return;
    }

    const owningType = activeTab === FlyerType.school
      ? OwningTypeEnum.district : OwningTypeEnum.organization;
    let filters: AllFlyerFilters = {
      owningType,
      searchQuery: textSearch,
      schoolIds: [],
      districtId: Number(district.districtId),
      isExpired: isExpired,
    }

    if (viewWholeCommunityFlyers !== null && owningType === OwningTypeEnum.organization) {
      filters = {
        ...filters,
        allDistrictSchools: true,
        isInMySchool: true
      }
    }
    //This state is used to retrieve the facets on the FlyerboardFilters based on the current filters
    flyerboardAllFiltersVar(filters);

    const { types, grades_age } = flyerboardActiveFilters;
    if(types.length > 0) {
      filters = {
        ...filters,
        categories: types,
      }
    }

    if(grades_age.length > 0) {
      filters = {
        ...filters,
        gradesOrAges: grades_age,
      }
    }

    try{
      setLoading(true);
      const { data } = await getAllFlyers({
        fetchPolicy: 'network-only',
        variables: {
          input: {
            filters,
            pagination: {
              pageNumber: page,
              pageSize: PAGE_SIZE
            }
          }
        }
      });

      const { items, pagination, facets } = data?.getAllFlyers || { items: [], pagination: {} };
      const paginationTotal = pagination?.total || 0;

      if(items && items?.length > 0) {
        trackImpressions({
          districtId: district.districtId.toString(),
          flyerType: activeTab,
          audienceId,
          flyers: items,
        });
        if(shouldRequestExpiredFlyers) {
          setExpiredFlyers((prevData) => [...prevData, ...items])
        }
        else {
          setFlyers((prevData) => [...prevData, ...items]);
        }
      }

      if(paginationTotal > 0) {
        if(shouldRequestExpiredFlyers) {
          setExpiredFlyersTotal(paginationTotal)
        }
        else {
          setTotal(paginationTotal);
          flyerboardFlyersAmountVar(paginationTotal);
        }
      }
      // should show the empty state only after retrieve the expired flyers
      if(isExpired && total === 0) {
        setDataNotFound(true);
      }

      if (facets && !isExpired) {
        filterFacetsVar(facets);
      }
      // when the query doesn't return any active result, it should make another request
      // to check if there are expired flyers
      if ((page === pages && !isExpired ||
          page === 1 && items.length === 0)
          && !shouldRequestExpiredFlyers
        ) {
        setIsExpired(true);
        setShouldRequestExpiredFlyers(true);
        setTriggerFetch(prev => !prev);
      }

    }catch(err) {
      console.log(err);
    }
    finally {
      setLoading(false)
    }
  }

  const selectedSubscriptions = useMemo(
    () =>
      sortBy(
        subscriptions,
        subscription => subscription.name,
      ),
    [subscriptions],
  );

  const labelViewMoreFlyers = useMemo(
    () =>
      `View ${
        activeTab === FlyerType.school ? 'community' : 'school'
      } search results`,
    [activeTab],
  );

  const nextPage = () => {
    setPage((prevPage) => prevPage + 1)
  }

  const favoriteFlyerInMemory = (flyerId: number, flyers: FlyerCardThumbnail[]) => {
    const index = flyers.findIndex(flyer => flyer.flyerId === flyerId);
    const newFlyers = [...flyers];
    newFlyers[index] = {
      ...newFlyers[index],
      isFavorite: !newFlyers[index].isFavorite,
    };
    return newFlyers;
  };

  const handleClickFavoriteFlyer = (flyerId: number) => {
    if(flyers.find(flyer => flyer.flyerId === flyerId)) {
      setFlyers(favoriteFlyerInMemory(flyerId, flyers))
    }
    else {
      setExpiredFlyers(favoriteFlyerInMemory(flyerId, expiredFlyers))
    }
  };
  const onClickViewExpiredFlyers = () => {
    setShowExpiredFlyers(true);
  }

  const renderInfiniteScroll = (
    { flyers, total, isExpired = false }
    : { flyers: FlyerCardThumbnail[], total: number, isExpired?: boolean; }
  ) => {
    const flyersTotal = flyers.length;
    const hasMore = flyersTotal < total;

    return (
      <InfiniteScroll
        dataLength={flyersTotal}
        next={nextPage}
        hasMore={hasMore}
        scrollThreshold={0.6}
        loader={<LoadingSpinner />}
      >
        <Masonry
          breakpointCols={MasonryBreakpointCols}
          className="my-masonry-grid"
          columnClassName="my-masonry-grid_column"
        >
          {flyers.map(
            ({ flyerId, frontImageUrl, isFavorite, title, status, email, pages }) => (
              <FlyerCard
                key={flyerId}
                id={flyerId}
                title={title}
                thumbnail={frontImageUrl}
                isFavorite={isFavorite}
                onFavoriteFlyer={handleClickFavoriteFlyer}
                inactive={isExpired}
                //FIXME: the API is returning expired flyers as active
                status={isExpired ? FlyerStatus.expired : status}
                flyerType={activeTab}
                audienceId={audienceId}
                email={email}
                ocrText={
                  pages && pages[0] ? pages[0].ocrText : ''
                }
              />
            ),
          )}
        </Masonry>
      </InfiniteScroll>
    )
  };

  const activeFlyersList = useMemo(() =>
    flyers.length > 0 && renderInfiniteScroll({ flyers, total }),
  [flyers, isExpired, showExpiredFlyers]);

  const expiredFlyersList = useMemo(() =>
    expiredFlyers.length > 0 &&
     (
      <div className={styles.section}>
        <div className={styles.dividerPadding}>
            <Divider />
        </div>
        <div className={styles.expiredTitle}>
          <BodyLargeEmphasized color={colors.slate}>
            Expired Flyers
          </BodyLargeEmphasized>
        </div>
        {renderInfiniteScroll({
          flyers: expiredFlyers,
          total: expiredFlyersTotal,
          isExpired: true
        })}
      </div>
    ),
  [expiredFlyers, showExpiredFlyers, ]);

  const onBackButtonClick = () => {
    navigate(`/explore?tab=${activeTab}`);
  };
  const onResetTextSearchInput = () => {
    navigate(APP_ROUTES.search);
  };

  const resetFilters = () => {
    clearActiveFilters();
    setShowExpiredFlyers(false);
  };

  const handleClickFlyerType = () => {
    track({
      category: `${trackId}:view_new_flyers:bottom_button`,
      action: GenericTrackTypes.BUTTON_PRESSED,
      label: FlyerType.school === activeTab ? 'Community' : 'School',
    });
    onChangeFlyerType();
  };


  const EmptyStates = useCallback(() => {
    if (!loading && dataNotFound) {
      if (flyers.length === 0 && isFiltering) {
        return (
          <EmptyState
            title="Currently no flyers that match your filter."
            description="Reset your filter to view other flyers."
            bottom={
              <div className={styles.link} onClick={() => resetFilters()}>
                Reset Filters
              </div>
            }
            illustration={turtleIllustration}
          />
        );
      }
      if (textSearchNotFound) {
        if(expiredFlyers.length > 0) {
          return <NoActiveFlyersFound searchTerm={searchTerm} />
        }
        return <NoSearchResultsFoundEmptyState searchTerm={searchTerm} />
      }
    }
    return null;
  }, [loading, dataNotFound, flyers, textSearchNotFound, expiredFlyers, searchTerm]);

  return (
    <div ref={ref}>
      <div className={styles.container}>
        <HeaderFixed>
          <NavbarSwitcher
            trackId={trackId}
            textSearch={textSearch}
            isTextBold
            showSearchBar
            setTextSearch={setTextSearch}
            onBackButtonClick={onBackButtonClick}
            onKeyDown={onKeyDown}
          />
          <FlyerboardHeader
            selectedAudienceId={audienceId}
            subscriptions={selectedSubscriptions}
            type={activeTab}
          />
        </HeaderFixed>
        <main className={styles.content}>
          <div className={styles.section}>
            {loading && flyers.length === 0 && expiredFlyers.length === 0 && (
              <LoadingSpinner />
            )}
            <EmptyStates />

            {activeFlyersList}
          </div>
          {flyers.length > 0 && isExpired && expiredFlyers.length > 0 && !showExpiredFlyers && (
            <ViewMoreCard
              title='EXPIRED FLYERS'
              description='Although flyers expire on Peachjar, the flyer opportunity or information may still be relevant or informative.'
              buttonLabel='View expired flyers'
              onClick={onClickViewExpiredFlyers}
            />
          )}
          {/* should show expired flyers if didn't find active results */}
          {(showExpiredFlyers ||
            (expiredFlyers.length > 0 && flyers.length === 0)) && (
            <>
              {expiredFlyersList}
            </>
          )}
        </main>
        <div className={styles.footer}>
          <BottomViewFlyers
            label={labelViewMoreFlyers}
            variant={FlyerType[activeTab]}
            onClick={handleClickFlyerType}
          />
        </div>
      </div>
    </div>
  );
};
